import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SideBarContainer from 'Containers/SideBarContainer';
import { Padding } from 'Components/Padding';
import { IconToggleGroup, type IconToggleOption } from 'Components/IconToggleGroup';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import SessionsReportKpis from './components/SessionsReportKpis';
import SessionsReportFilters from './components/SessionsReportFilters';
import SessionsReportGroupedList from './components/SessionsReportGroupedList';
import useSessionsReportFilters from './hooks/useSessionsReportFilters';
import useSessionsReportPage from './hooks/useSessionsReportPage';
import { sessionsReportFilterDefaults, type SessionsReportFilterValues } from './schemas/sessions-report-schemas';

/**
 * Sessions Report page.
 *
 * SRP: composes filters + report data + grouped list. Owns no business rules
 * beyond translating UX gestures (filter changes, export click) into hook
 * calls.
 *
 * Per-employee mode is activated by `?userId=<uuid>` in the URL — same page,
 * different read mode (US4).
 */
export default function SessionsReportPage() {
  const history = useHistory();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const userIdFromUrl = params.get('userId') || undefined;

  const form = useSessionsReportFilters(
    userIdFromUrl ? { ...sessionsReportFilterDefaults, userId: userIdFromUrl } : undefined
  );
  const { handleSubmit } = form;

  const { report, loading, error, exporting, fetchReport, handleExport, t2 } = useSessionsReportPage({
    initialUserId: userIdFromUrl,
  });

  type SessionsViewMode = 'employees' | 'courses';
  const viewMode: SessionsViewMode = 'courses';

  const viewModeOptions: IconToggleOption<SessionsViewMode>[] = [
    {
      value: 'employees',
      tooltip: 'Por empleados',
      icon: <EmployeesReportIcon />,
    },
    {
      value: 'courses',
      tooltip: 'Por cursos',
      icon: <CoursesReportIcon />,
    },
  ];

  const handleViewModeChange = (value: SessionsViewMode) => {
    if (value === viewMode) return;
    history.push(value === 'employees' ? '/reporte/sesiones/empleados' : '/reporte/sesiones/cursos');
  };

  // Initial fetch on mount using the default filter values (last 6 months).
  useEffect(() => {
    handleSubmit((values: SessionsReportFilterValues) => fetchReport(values))();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClick = (sessionId: string) => {
    history.push(`/reporte/sesiones/${sessionId}`);
  };

  return (
    <SideBarContainer>
      <ReportsLayout
        title={<ReportsTitle title={t2('pageTitle')} accent={`"${t2('pageAccent')}"`} />}
        onExport={handleExport}
        buttons={
          <IconToggleGroup
            options={viewModeOptions}
            value={viewMode}
            onChange={handleViewModeChange}
            ariaLabel="Modo de vista"
          />
        }
      />
      <Padding padding="1rem">
        <SessionsReportFilters form={form} onChange={fetchReport} report={report} />
        <SessionsReportKpis kpis={report?.kpis ?? null} loading={loading} />
        <SessionsReportGroupedList
          report={report}
          loading={loading || exporting}
          error={error}
          showIndividualAttendance={!!userIdFromUrl}
          onRowClick={handleRowClick}
        />
      </Padding>
    </SideBarContainer>
  );
}
